import type { InvoiceStatus, MasterRef } from '@/lib/types';
import { Badge, type BadgeTone } from '@/components/ui';
import { Money } from '@/components/ui/Money';
import { isStatusSettled, normaliseStatusName } from '@/lib/invoice-utils';

const stroke = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/** Empty-state glyph for invoices — a document with a currency mark on it. */
export function InvoiceIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z" />
      <path d="M14 2v5h5" />
      <path d="M12 11v7" />
      <path d="M14 12.5A1.75 1.75 0 0 0 12.25 11h-.5a1.75 1.75 0 0 0 0 3.5h.5a1.75 1.75 0 0 1 0 3.5h-.5A1.75 1.75 0 0 1 10 16.5" />
    </svg>
  );
}

/**
 * WHICH settled colour a settled status gets. Consulted only after
 * `isStatusSettled()` has already said the money is no longer owed.
 *
 * ⚠️ **THIS NO LONGER DECIDES WHETHER A STATUS IS SETTLED**, which is the whole
 * change. Migration 080 put `is_settled` on `f_invoice_statuses`, so that
 * question is now the server's. Paid and Cancelled are both settled and want
 * different colours — one is money received, the other is money that will never
 * arrive — and no single boolean can express that, so a name map survives here
 * for the narrow job of telling those two apart.
 *
 * ⚠️ **The dangerous direction is now unreachable.** `success` exists only in
 * this map, and this map is only read on a row the server has classified as
 * settled. A status the server says is NOT settled therefore cannot be painted
 * green by its label, however it is renamed — which is exactly what
 * **"Unpaid" contains "paid"** used to do, on the one surface finance reads to
 * decide what to chase. A settled row whose name is unrecognised falls to
 * `neutral`: no colour, rather than a guessed one.
 *
 * ⚠️ **THE KNOWN DUPLICATION IS CLOSED.** This used to have to be edited in
 * step with `SETTLED_STATUS_NAMES` in `@/lib/invoice-utils`, with nothing
 * enforcing it, because both independently answered "is this settled". Only one
 * of them does now — that one, imported through `isStatusSettled()` — and this
 * map answers a different question, so they can no longer drift into
 * disagreeing about the same row. Membership here is deliberately not required
 * to match: a settled name missing from this map costs a colour, not a verdict.
 *
 * Matched EXACTLY, after case-folding and whitespace collapse, and NEVER by
 * containment. **Do not reintroduce `includes()`** — the flag removes the need
 * for the guess, it does not make the guess safe.
 */
const SETTLED_STATUS_TONES: ReadonlyMap<string, BadgeTone> = new Map<string, BadgeTone>([
  ['paid', 'success'],
  ['cancelled', 'danger'],
  ['canceled', 'danger'],
]);

/**
 * Tones for statuses where money is still owed. No `success` here, by
 * construction — see above.
 *
 * These are the rows migration 017 seeded that are not settled: Draft and
 * Awaiting Payment. Nothing else is guessed at, deliberately — a speculative
 * entry ("Overdue", "Part Paid") would make this look more authoritative than
 * it is.
 */
const UNSETTLED_STATUS_TONES: ReadonlyMap<string, BadgeTone> = new Map<string, BadgeTone>([
  ['awaiting payment', 'warning'],
  // Listed rather than left to the default, to record that Draft was considered.
  ['draft', 'neutral'],
]);

/**
 * An invoice's status.
 *
 * The label is always the server's own name, so the TEXT is never wrong here —
 * only the colour can be. That is a real mitigation and it is why this ranks
 * below `isOverdue()`. It is not a licence to guess, though: house style is
 * never colour as the only signal, so the colour is a SECOND signal agreeing
 * with the label, and a colour that disagrees tells the reader the label means
 * something it does not — which is worse than no colour at all.
 *
 * ⚠️ **Pass `statuses` wherever the page already has the master list.** An
 * invoice's own `status` is `{id, name}` and carries no flag, so without the
 * list the settled test falls back to matching names exactly. That fallback is
 * correct today and is precisely the guess the flag exists to replace.
 *
 * The tone and `isOverdue()` now read settledness through the SAME function, so
 * a row can no longer carry two opposite claims — a green status beside an
 * Overdue badge — which is what happened when the two were fixed separately.
 * A reader resolves that contradiction by trusting whichever they notice first.
 */
export function StatusBadge({
  status,
  statuses,
}: {
  status: MasterRef | null;
  statuses?: readonly InvoiceStatus[];
}) {
  if (!status) return <span className="text-xs text-fg-subtle">No status</span>;

  const name = normaliseStatusName(status.name);
  const tone: BadgeTone = isStatusSettled(status, statuses)
    ? (SETTLED_STATUS_TONES.get(name) ?? 'neutral')
    : (UNSETTLED_STATUS_TONES.get(name) ?? 'neutral');

  return (
    <Badge tone={tone} dot>
      {status.name}
    </Badge>
  );
}

/**
 * A money value with the invoice's own currency beside it.
 *
 * The code is always shown, never a symbol: there is no exchange rate anywhere
 * in this system by design, so a figure that does not say which currency it is
 * in is a figure someone will add to another one.
 *
 * `null` renders an em dash rather than zero. On these records that distinction
 * is real — `discountTotal` and `taxTotal` are null on every imported invoice,
 * meaning "the export never said", which is not the same as "nothing".
 */
export function InvoiceMoney({
  value,
  currency,
  emptyLabel = 'Not recorded',
}: {
  value: string | null;
  currency?: string | null;
  emptyLabel?: string;
}) {
  if (value === null) {
    return (
      <span className="text-fg-subtle" title={emptyLabel}>
        —
      </span>
    );
  }
  /*
   * The figure itself is the shared `Money` primitive, so an invoice amount reads
   * exactly like every other amount in the app. The `null` branch above stays
   * here because it carries this component's own `emptyLabel` on its `title`.
   */
  return <Money amount={value} currency={currency ?? null} className="whitespace-nowrap" />;
}
